import AddCropDialog from "@/components/settings/AddCropDialog";
import AddLocationDialog from "@/components/settings/AddLocationDialog";
import CropsList from "@/components/settings/CropsList";
import EditCropDialog from "@/components/settings/EditCropDialog";
import EditLocationDialog from "@/components/settings/EditLocationDialog";
import LocationsList from "@/components/settings/LocationsList";
import TransitionOverlay from "@/components/TransitionOverlay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { useRetainedValue } from "@/hooks/useRetainedValue";
import { useToast } from "@/hooks/use-toast";
import { useCrops, useLocations, useOrganization } from "@/hooks/useFirestore";
import { createCrop, updateCrop, deleteCrop, createLocation, updateLocation, deleteLocation } from "@/services/firebase";
import { Crop } from "@/types/crop";
import { Location } from "@/types/location";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

const Settings = () => {
  // Crop state
  const [isAddCropOpen, setIsAddCropOpen] = useState(false);
  const [isEditCropOpen, setIsEditCropOpen] = useState(false);
  const [selectedCrop, setSelectedCrop] = useState<Crop | null>(null);
  const [cropEditSession, setCropEditSession] = useState(0);

  // Location state
  const [isAddLocationOpen, setIsAddLocationOpen] = useState(false);
  const [isEditLocationOpen, setIsEditLocationOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locationEditSession, setLocationEditSession] = useState(0);

  // Deletion overlay state
  const [deletionOverlay, setDeletionOverlay] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
  } | null>(null);
  const renderedSelectedCrop = useRetainedValue(selectedCrop);
  const renderedSelectedLocation = useRetainedValue(selectedLocation);

  const { userRole } = useAuth();
  const { organization } = useOrganization();
  const { crops, loading: cropsLoading, error: cropsError } = useCrops();
  const { locations, loading: locationsLoading, error: locationsError } = useLocations();
  const { toast } = useToast();

  // Role-based permissions - same as Traits page
  const canEdit = userRole && ["editor", "admin"].includes(userRole);
  const canDelete = userRole && ["editor", "admin"].includes(userRole);

  const cropNames = useMemo(() => crops.map(c => c.name), [crops]);
  const locationNames = useMemo(() => locations.map(l => l.name), [locations]);

  // Crop handlers
  const handleAddCrop = async (data: { name: string; description?: string }) => {
    try {
      const created = await createCrop(data);
      if (created) {
        toast({
          title: "Crop added",
          description: `${data.name} has been added successfully`,
        });
        setIsAddCropOpen(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to add crop",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding crop:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add crop",
        variant: "destructive",
      });
    }
  };

  const handleEditCrop = (crop: Crop) => {
    setCropEditSession((current) => current + 1);
    setSelectedCrop(crop);
    setIsEditCropOpen(true);
  };

  const handleUpdateCrop = async (data: { name: string; description?: string }) => {
    if (!selectedCrop?.id) return;

    try {
      const success = await updateCrop(selectedCrop.id, data);
      if (success) {
        toast({
          title: "Crop updated",
          description: `${data.name} has been updated successfully`,
        });
        setIsEditCropOpen(false);
        setSelectedCrop(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to update crop",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating crop:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update crop",
        variant: "destructive",
      });
    }
  };

  const handleDeleteCrop = async (cropId: string) => {
    const crop = crops.find((c) => c.id === cropId);
    const cropName = crop?.name || "crop";

    setDeletionOverlay({
      open: true,
      title: "Deleting crop...",
      subtitle: `Removing "${cropName}"...`,
    });

    try {
      const success = await deleteCrop(cropId);
      if (success) {
        toast({
          title: "Crop deleted",
          description: "The crop has been deleted successfully",
        });
        setIsEditCropOpen(false);
        setSelectedCrop(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to delete crop",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting crop:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete crop",
        variant: "destructive",
      });
    } finally {
      setDeletionOverlay(null);
    }
  };

  // Location handlers
  const handleAddLocation = async (data: { name: string; description?: string }) => {
    try {
      const created = await createLocation(data);
      if (created) {
        toast({
          title: "Location added",
          description: `${data.name} has been added successfully`,
        });
        setIsAddLocationOpen(false);
      } else {
        toast({
          title: "Error",
          description: "Failed to add location",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add location",
        variant: "destructive",
      });
    }
  };

  const handleEditLocation = (location: Location) => {
    setLocationEditSession((current) => current + 1);
    setSelectedLocation(location);
    setIsEditLocationOpen(true);
  };

  const handleUpdateLocation = async (data: { name: string; description?: string }) => {
    if (!selectedLocation?.id) return;

    try {
      const success = await updateLocation(selectedLocation.id, data);
      if (success) {
        toast({
          title: "Location updated",
          description: `${data.name} has been updated successfully`,
        });
        setIsEditLocationOpen(false);
        setSelectedLocation(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to update location",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update location",
        variant: "destructive",
      });
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    const location = locations.find((l) => l.id === locationId);
    const locationName = location?.name || "location";

    setDeletionOverlay({
      open: true,
      title: "Deleting location...",
      subtitle: `Removing "${locationName}"...`,
    });

    try {
      const success = await deleteLocation(locationId);
      if (success) {
        toast({
          title: "Location deleted",
          description: "The location has been deleted successfully",
        });
        setIsEditLocationOpen(false);
        setSelectedLocation(null);
      } else {
        toast({
          title: "Error",
          description: "Failed to delete location",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete location",
        variant: "destructive",
      });
    } finally {
      setDeletionOverlay(null);
    }
  };

  const loading = cropsLoading || locationsLoading;
  const error = cropsError || locationsError;

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">Error loading settings: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <TransitionOverlay
        open={deletionOverlay?.open ?? false}
        title={deletionOverlay?.title ?? ""}
        subtitle={deletionOverlay?.subtitle}
      />

      <div className="mb-6">
        <h1 className="text-2xl font-bold">
          {organization?.name ? `${organization.name} Settings` : "Settings"}
        </h1>
      </div>

      {/* Crops Card */}
      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Crops</CardTitle>
            <CardDescription>Manage crops for your organization</CardDescription>
          </div>
          {canEdit && (
            <Button variant="default" onClick={() => setIsAddCropOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add Crop
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <CropsList crops={crops} onEditCrop={handleEditCrop} canEdit={canEdit} />
        </CardContent>
      </Card>

      {/* Locations Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Locations</CardTitle>
            <CardDescription>Manage locations for your organization</CardDescription>
          </div>
          {canEdit && (
            <Button variant="default" onClick={() => setIsAddLocationOpen(true)}>
              <Plus className="mr-1 h-4 w-4" /> Add Location
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <LocationsList locations={locations} onEditLocation={handleEditLocation} canEdit={canEdit} />
        </CardContent>
      </Card>

      {/* Crop Dialogs */}
      <AddCropDialog
        open={isAddCropOpen}
        onOpenChange={setIsAddCropOpen}
        onSubmit={handleAddCrop}
        existingNames={cropNames}
      />

      <EditCropDialog
        key={renderedSelectedCrop ? `${renderedSelectedCrop.id}-${cropEditSession}` : "no-crop-selected"}
        open={isEditCropOpen}
        onOpenChange={setIsEditCropOpen}
        crop={renderedSelectedCrop}
        onSubmit={handleUpdateCrop}
        onDelete={handleDeleteCrop}
        canDelete={canDelete}
        existingNames={cropNames}
      />

      {/* Location Dialogs */}
      <AddLocationDialog
        open={isAddLocationOpen}
        onOpenChange={setIsAddLocationOpen}
        onSubmit={handleAddLocation}
        existingNames={locationNames}
      />

      <EditLocationDialog
        key={renderedSelectedLocation ? `${renderedSelectedLocation.id}-${locationEditSession}` : "no-location-selected"}
        open={isEditLocationOpen}
        onOpenChange={setIsEditLocationOpen}
        location={renderedSelectedLocation}
        onSubmit={handleUpdateLocation}
        onDelete={handleDeleteLocation}
        canDelete={canDelete}
        existingNames={locationNames}
      />
    </div>
  );
};

export default Settings;
